import { Dispatch, useCallback, useState } from 'react';
import { Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { getCountryCodeForRegionCode, parsePhoneNumber } from 'awesome-phonenumber';
import { parseAndFormatTelephone, replaceTelephoneCallingCode } from '~/adapters/telephone/telephone';
import { useMenu } from '~/components/hooks/useMenu';
import {
    countryCodesSortedByFullName,
    countryNameByCode,
    defaultCountryCode,
} from '~/adapters/geography/countriesAndProvinces';
import { FlagIcon } from '~/components/FlagIcon/FlagIcon';
import { AddressFormState, ValidatedAddressDialogFieldProps } from '../../types';
import { useErrorProps } from '../useErrorProps';
import { tooLongErrorMessage } from '../constants';
import { AutofillTextField } from '../AutofillTextField';

interface CallingCodeDropdownProps {
    telephone: string;
    setTelephone: Dispatch<string>;
    telephoneCountryCode: string;
    setTelephoneCountryCode: Dispatch<string>;
}

export const CallingCodeSelect = ({
    telephone,
    setTelephone,
    telephoneCountryCode,
    setTelephoneCountryCode,
}: CallingCodeDropdownProps) => {
    const { anchorEl, handleOpen, handleClose } = useMenu();

    const handleCountrySelect = useCallback(
        (newCountryCode) => {
            setTelephoneCountryCode(newCountryCode);
            const newCallingCode = getCountryCodeForRegionCode(newCountryCode);
            const newTelephone = replaceTelephoneCallingCode(telephone, newCallingCode);
            const { formattedTelephone } = parseAndFormatTelephone(newTelephone);
            setTelephone(formattedTelephone);
            handleClose();
        },
        [handleClose, telephone],
    );

    return (
        <>
            <IconButton onClick={handleOpen} style={{ borderRadius: 1, width: 'initial', height: 'initial' }}>
                <FlagIcon countryCode={telephoneCountryCode} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                id="calling-code-menu"
                MenuListProps={{ 'aria-labelledby': 'more-menu' }}
            >
                {countryCodesSortedByFullName.map((countryCode) => (
                    <MenuItem key={countryCode} onClick={() => handleCountrySelect(countryCode)}>
                        {countryNameByCode[countryCode]} (+{getCountryCodeForRegionCode(countryCode)})
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export type TelephoneProps = ValidatedAddressDialogFieldProps & { setValue: UseFormSetValue<AddressFormState> };

export const Telephone = ({ control, fieldErrors, setValue }: TelephoneProps) => {
    const { error, helperText } = useErrorProps('telephone', fieldErrors);

    const telephone = useWatch({ control, name: 'telephone' });
    const setTelephone = (value: string) => setValue('telephone', value);

    // try to parse region code from the initial value of telephone number, or preselect the default country
    const getInitialCountryCode = (): string => parsePhoneNumber(telephone).regionCode || defaultCountryCode;
    const [telephoneCountryCode, setTelephoneCountryCode] = useState(getInitialCountryCode);

    const handleBlur = useCallback(() => {
        const { formattedTelephone, parsedPhoneNumber } = parseAndFormatTelephone(telephone, telephoneCountryCode);
        setTelephone(formattedTelephone);
        // try to autodetect region code from phone number
        if (parsedPhoneNumber.regionCode) {
            setTelephoneCountryCode(parsedPhoneNumber.regionCode);
        }
    }, [telephone]);

    return (
        <Controller
            name="telephone"
            control={control}
            rules={{ maxLength: { value: 50, message: tooLongErrorMessage } }}
            render={({ field }) => (
                <AutofillTextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...field}
                    error={error}
                    InputProps={{
                        startAdornment: (
                            <CallingCodeSelect
                                telephone={telephone}
                                setTelephone={setTelephone}
                                telephoneCountryCode={telephoneCountryCode}
                                setTelephoneCountryCode={setTelephoneCountryCode}
                            />
                        ),
                        // padding of the outlined box (remove excessive space before CallingCodeSelect)
                        style: { paddingLeft: 0 },
                    }}
                    // padding of the input element (remove excessive space between the CallingCodeSelect and the input)
                    sx={{ '& .MuiInputBase-input ': { paddingLeft: 0 } }}
                    onBlur={handleBlur}
                    helperText={helperText}
                    label="Phone Number (Optional)"
                    autoFillKey="tel"
                    placeholder="(123) 456-7890"
                    fullWidth
                />
            )}
        />
    );
};
